/**
 * Transaction History Modal
 * Shows user's credit transaction history
 */

import { authService } from '../auth/auth-service.js';
import { SUPABASE_URL } from '../config/config.js';
import { escapeHtml } from '../lib/html-utils.js';

class TransactionHistory {
  constructor() {
    this.modal = null;
    this.isOpen = false;
    this.transactions = [];
    this.balance = 0;
    this.lifetimePurchased = 0;
    this.total = 0;
    this.offset = 0;
    this.limit = 20;
  }

  /**
   * Initialize the transaction history modal
   */
  init() {
    this.createModal();
    this.attachEventListeners();
  }

  /**
   * Create the modal HTML
   */
  createModal() {
    const modalHTML = `
      <div id="transactionHistoryModal" class="transaction-modal" style="display: none;">
        <div class="transaction-modal-backdrop"></div>
        <div class="transaction-modal-content">
          <button class="transaction-modal-close">&times;</button>

          <h2 class="transaction-title">Credit History</h2>

          <div class="transaction-summary">
            <div class="transaction-balance">
              <span class="balance-label">Current Balance</span>
              <span class="balance-value" id="transactionBalance">0</span>
            </div>
            <div class="transaction-lifetime">
              <span class="lifetime-label">Lifetime Purchased</span>
              <span class="lifetime-value" id="transactionLifetime">0</span>
            </div>
          </div>

          <div class="transaction-list" id="transactionList">
            <div class="transaction-loading">Loading...</div>
          </div>

          <div class="transaction-pagination" id="transactionPagination" style="display: none;">
            <button class="btn btn-small" id="transactionPrev" disabled>Previous</button>
            <span class="transaction-page-info" id="transactionPageInfo">Page 1</span>
            <button class="btn btn-small" id="transactionNext">Next</button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('transactionHistoryModal');
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Close modal
    this.modal.querySelector('.transaction-modal-backdrop').addEventListener('click', () => this.hide());
    this.modal.querySelector('.transaction-modal-close').addEventListener('click', () => this.hide());

    // Pagination
    document.getElementById('transactionPrev')?.addEventListener('click', () => this.loadPage(-1));
    document.getElementById('transactionNext')?.addEventListener('click', () => this.loadPage(1));

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.hide();
      }
    });
  }

  /**
   * Show the modal
   */
  async show() {
    this.modal.style.display = 'flex';
    this.isOpen = true;
    this.offset = 0;
    await this.fetchTransactions();
  }

  /**
   * Hide the modal
   */
  hide() {
    this.modal.style.display = 'none';
    this.isOpen = false;
  }

  /**
   * Load a different page
   */
  async loadPage(direction) {
    const newOffset = this.offset + (direction * this.limit);
    if (newOffset < 0 || newOffset >= this.total) return;
    this.offset = newOffset;
    await this.fetchTransactions();
  }

  /**
   * Fetch transactions from the server
   */
  async fetchTransactions() {
    const listEl = document.getElementById('transactionList');
    listEl.innerHTML = '<div class="transaction-loading">Loading...</div>';

    if (!authService.isAuthenticated()) {
      listEl.innerHTML = '<div class="transaction-empty">Please log in to view transaction history.</div>';
      return;
    }

    try {
      const accessToken = authService.getAccessToken();
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/get-transactions?limit=${this.limit}&offset=${this.offset}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch transactions');
      }

      const data = await response.json();
      this.transactions = data.transactions;
      this.total = data.total;
      this.balance = data.balance;
      this.lifetimePurchased = data.lifetimePurchased;

      this.renderTransactions();
      this.updateSummary();
      this.updatePagination();

    } catch (error) {
      console.error('Error fetching transactions:', error);
      listEl.innerHTML = '<div class="transaction-error">Failed to load transactions. Please try again.</div>';
    }
  }

  /**
   * Render the transaction list
   */
  renderTransactions() {
    const listEl = document.getElementById('transactionList');

    if (this.transactions.length === 0) {
      listEl.innerHTML = '<div class="transaction-empty">No transactions yet.</div>';
      return;
    }

    const transactionsHTML = this.transactions.map(tx => this.renderTransaction(tx)).join('');
    listEl.innerHTML = `<div class="transaction-items">${transactionsHTML}</div>`;
  }

  /**
   * Render a single transaction
   */
  renderTransaction(tx) {
    const date = new Date(tx.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const isPositive = tx.amount > 0;
    const amountClass = isPositive ? 'transaction-amount-positive' : 'transaction-amount-negative';
    const amountPrefix = isPositive ? '+' : '';

    const reasonLabels = {
      'purchase': 'Credit Purchase',
      'export': 'Video Export',
      'refund': 'Refund',
      'bonus': 'Bonus Credits'
    };

    const reasonIcons = {
      'purchase': '&#128176;', // money bag
      'export': '&#127916;', // movie camera
      'refund': '&#8634;', // return arrow
      'bonus': '&#127873;' // gift
    };

    return `
      <div class="transaction-item">
        <div class="transaction-icon">${reasonIcons[tx.reason] || '&#9679;'}</div>
        <div class="transaction-details">
          <span class="transaction-reason">${reasonLabels[tx.reason] || escapeHtml(tx.reason)}</span>
          <span class="transaction-date">${date}</span>
        </div>
        <div class="transaction-amount ${amountClass}">
          ${amountPrefix}${tx.amount}
        </div>
      </div>
    `;
  }

  /**
   * Update summary display
   */
  updateSummary() {
    document.getElementById('transactionBalance').textContent = this.balance;
    document.getElementById('transactionLifetime').textContent = this.lifetimePurchased;
  }

  /**
   * Update pagination controls
   */
  updatePagination() {
    const paginationEl = document.getElementById('transactionPagination');
    const prevBtn = document.getElementById('transactionPrev');
    const nextBtn = document.getElementById('transactionNext');
    const pageInfo = document.getElementById('transactionPageInfo');

    if (this.total <= this.limit) {
      paginationEl.style.display = 'none';
      return;
    }

    paginationEl.style.display = 'flex';

    const currentPage = Math.floor(this.offset / this.limit) + 1;
    const totalPages = Math.ceil(this.total / this.limit);

    pageInfo.textContent = `Page ${currentPage} of ${totalPages}`;
    prevBtn.disabled = this.offset === 0;
    nextBtn.disabled = this.offset + this.limit >= this.total;
  }
}

// Export singleton instance
export const transactionHistory = new TransactionHistory();
